import { useEffect, useState } from 'react';
import { Check, ChevronRight, PencilLine } from 'lucide-react';
import toast from 'react-hot-toast';
import { connect } from 'react-redux';
import MedicalHistory from 'components/Profile/MedicalHistory/Form';
import Insurance from 'components/Profile/Insurance/Form';
import { PortalSpinner } from 'components/PortalSpinner';
import { getPatient } from 'library/auth';
import { ProfileActions } from 'store/actions';
import InsuranceReview from './InsuranceReview';
import { getUpdates, MEDICAL_FORM_ID, INSURANCE_FORM_ID, PRIMARY_BTN, SECONDARY_BTN } from './helper';
import Header from './Header';

const enhancer = connect(null, { ...ProfileActions });

const Success = ({ showProfile, visitDate, startTime, onDone }) => {
  const [step, setStep] = useState(null);
  const [profile, setProfile] = useState(null);

  const { insurance_intake, medical_history, practice } = profile?.attributes || {};
  const insurance = insurance_intake?.data?.attributes;
  const medicalHistory = medical_history?.data?.attributes;

  const fullName = getPatient()?.full_name;
  const firstName = fullName?.trim().split(' ')[0];

  useEffect(() => {
    async function fetchProfile() {
      try {
        const { data } = await showProfile();
        setProfile(data);
      } catch (error: any) {
        toast.error(error.errors || 'Failed to load profile. Please try again later.');
      }
    }

    fetchProfile();
  }, []);

  function handleSaved(section, data) {
    setStep(null);
    setProfile((p: any) => ({ ...p, attributes: { ...p.attributes, [section]: { data } } }));
  }

  if (step) {
    const isReview = step === 'insurance';

    const body = !profile ? (
      <PortalSpinner padding="py-16" />
    ) : step === 'medical' ? (
      <MedicalHistory
        medicalHistory={medicalHistory || {}}
        onSaved={(data) => handleSaved('medical_history', data)}
        inModal
        formId={MEDICAL_FORM_ID}
      />
    ) : isReview ? (
      <>
        <Header
          visitDate={visitDate}
          startTime={startTime}
          firstName={firstName}
        />

        <div className="mt-5">
          <InsuranceReview data={insurance} memberName={fullName} />
        </div>
      </>
    ) : (
      <Insurance
        insurance={insurance || {}}
        practice={practice?.data?.attributes}
        onSaved={(data) => handleSaved('insurance_intake', data)}
        inModal
        formId={INSURANCE_FORM_ID}
      />
    );

    return (
      <>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5 pt-6 sm:px-7">{body}</div>

        <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-gray-100 px-6 py-3.5 sm:px-7">
          <button
            type="button"
            onClick={() => setStep(step === 'insurance-edit' ? 'insurance' : null)}
            className={SECONDARY_BTN}
          >
            {isReview ? 'Back' : 'Cancel'}
          </button>

          {isReview ? (
            <button key="edit" type="button" onClick={() => setStep('insurance-edit')} className={PRIMARY_BTN}>
              <PencilLine size={16} />
              Update insurance
            </button>
          ) : (
            <button
              key="save"
              type="submit"
              form={step === 'medical' ? MEDICAL_FORM_ID : INSURANCE_FORM_ID}
              className={PRIMARY_BTN}
            >
              <Check size={16} strokeWidth={2.8} />
              Save changes
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="overflow-y-auto px-6 pb-6 pt-8 sm:px-7">
      <Header
        visitDate={visitDate}
        startTime={startTime}
        firstName={firstName}
      />

      <div className="mt-5 rounded-xl border border-gray-200 p-3.5 text-left">
        <p className="text-base font-bold text-secondary-900">Anything you&apos;d like us to know?</p>
        <p className="mt-1 text-sm leading-relaxed text-secondary-500">
          No need to look anything up — only tap below if something has actually changed.
        </p>

        <div className="mt-3.5 space-y-2.5">
          {getUpdates(insurance).map(({ id, icon: Icon, iconClass, title, description }) => (
            <button
              key={id}
              type="button"
              onClick={() => setStep(id)}
              className="flex w-full items-center gap-3 rounded-lg border border-gray-200 px-3.5 py-2.5 text-left transition-colors hover:bg-gray-50"
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconClass}`}>
                <Icon size={17} />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-secondary-900">{title}</span>
                {description && <span className="mt-0.5 block text-sm text-secondary-500">{description}</span>}
              </span>

              <ChevronRight size={17} className="shrink-0 text-secondary-300" />
            </button>
          ))}
        </div>
      </div>

      <button type="button" onClick={onDone} className={`${PRIMARY_BTN} mt-5 w-full`}>
        <Check size={16} strokeWidth={2.8} />
        Done
      </button>
    </div>
  );
};

export default enhancer(Success);
